// localStorage-backed token store. Tokens live across reloads + browser
// restarts — admin sessions span hours of menu review, and the alternative
// (sessionStorage or in-memory) would force a re-sign-in every refresh.
//
// XSS exposure: tokens in localStorage are readable by any JS that runs on
// the page. The mitigation is a strict CSP + no third-party scripts in this
// app (the index.html only loads our own /src/main.tsx). If we later add a
// dependency that injects JS at runtime, revisit this.

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** Absolute epoch ms when id_token expires. Reads of `current()` clear
   *  the store if the id_token is past expiry — refresh is the caller's
   *  responsibility (auth-service refreshIfNeeded). */
  expiresAt: number;
}

const STORAGE_KEY = 'tee-admin-tokens';

export function save(tokens: StoredTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

/** Returns the stored tokens if any. Does NOT validate freshness — that's
 *  the caller's job so refresh logic stays in one place. */
export function load(): StoredTokens | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clear(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export type { StoredTokens };
